import {
  type CarbonIconType as LucideIcon,
  Group as Users,
  UserCertification as UserCheck,
  Book as BookOpen,
  Layers,
  SecurityServices as ShieldCheck,
  Activity,
  CheckmarkOutline as CheckCircle,
  Lightning as Zap,
} from '@carbon/icons-react'

/**
 * The seven customer stories, moved verbatim out of
 * app/customer-stories/[slug]/page.tsx. Not one string was retyped or reworded.
 *
 * ONE mechanical change: `stats.*.icon` held 21 JSX elements
 * (`<Users className="w-4 h-4" />`). They are now plain lucide component
 * references, because a React element cannot live in a module that a Server
 * Component imports, and rendering is the view's job anyway.
 *
 * This is the single source of truth: the listing page and the detail route
 * both read it. Previously the listing kept its own `caseStudiesData` with a
 * different field set, which is exactly how copy drifts.
 */

export type StoryStat = { value: string; label: string; icon: LucideIcon }

export const customerStories = [
  {
    slug: 'dypcet',
    institutionName: 'D. Y. Patil College of Engineering and Technology',
    institutionSubtitle:
      'Modern Website, Admin Control & AI Interview Platform',
    quote:
      'The solutions implemented by IntelQI helped us modernize both our institutional operations and digital experience.',
    logo: '/stories/dyp.webp',
    herologo: '/stories/herodypcet.webp',
    institution: 'D. Y. Patil College of Engineering and Technology',
    location: 'Kolhapur, Maharashtra',
    category: 'Engineering College',
    challenge:
      'DYPCET aimed to modernize its digital presence while enabling internal teams to independently manage website content and updates without technical dependency. The institution also required a smarter interview management solution capable of streamlining candidate evaluation processes.',
    journey: [
      'No centralized system for managing website content. Every update required technical intervention.',
      'Interview workflows were manual, leading to inconsistency and coordination overhead.',
      "The institutional website did not reflect DYPCET's scale or digital ambitions.",
      'Administrative teams lacked control over announcements, notices, and institutional information.',
    ],
    journeyDescription:
      'To strengthen its digital infrastructure and institutional outreach, D. Y. Patil College of Engineering & Technology partnered with IntelQI for a comprehensive digital transformation initiative covering a redesigned website, a powerful admin panel, and an AI-driven interview portal.',
    solution:
      'IntelQI delivered two independent platforms for DYPCET. The first: a modern, responsive institutional website with dynamic marketing pages and improved navigation, paired with a centralized admin panel so DYPCET teams can independently update content without technical intervention. The second: a standalone AI Interview Portal to support structured, technology-driven interview processes with centralized candidate records and improved transparency. Both platforms operate independently with no shared infrastructure.',
    whatWeBuilt: [
      {
        title: 'Redesigned institutional website',
        description:
          'Fully responsive architecture, dynamic marketing pages, improved navigation, and enhanced branding built from the ground up.',
        tag: 'Website Revamp',
      },
      {
        title: 'Self-managed admin panel',
        description:
          'Update content, publish announcements, and modify pages in real time. No developer needed for any change.',
        tag: 'Admin Panel',
      },
      {
        title: 'AI-assisted interview portal',
        description:
          'Add job requirements per role, configure domain-specific questions, attach job descriptions, and deploy structured interview sessions.',
        tag: 'AI Interview Portal',
      },
      {
        title: 'Domain-wise interview analytics',
        description:
          'Detailed reports per role and interview cycle: candidates interviewed, branch-wise participation, evaluation summaries, and shortlisting insights.',
        tag: 'Analytics',
      },
    ],
    whatWeBuiltDescription:
      'Two independent platforms: a modern institutional website with admin control, and a standalone AI-powered interview portal, each purpose-built and delivered separately.',
    milestones: [
      'Enhanced institutional digital presence with a modern, fully responsive website.',
      'Internal teams now manage website content independently through the admin panel.',
      'AI Interview Portal operational with structured workflows and centralized candidate records.',
      'Domain-wise interview reports and analytical dashboards deployed.',
      'Administrative overhead significantly reduced across website and interview operations.',
    ],
    outcome:
      'The solutions implemented by IntelQI helped us modernize both our institutional operations and digital experience.',
    stats: {
      students: {
        value: '5000+',
        label: 'Students',
        icon: Users,
      },
      faculty: {
        value: '300+',
        label: 'Faculty',
        icon: UserCheck,
      },
      programs: {
        value: '15+',
        label: 'Programs',
        icon: BookOpen,
      },
    },
    testimonial: {
      name: 'Dr. Santosh Chede',
      role: 'Principal, DYPCET Kolhapur',
      quote:
        'The admin panel gave us greater control and flexibility in managing our institutional website efficiently.',
    },
    technologies: [
      'AI Interview Portal',
      'Website Revamp',
      'Admin Panel',
      'Analytics',
    ],
    showcase: [
      {
        tag: 'Website and Admin Panel',
        heading: 'Your team updates the website. No calls to IT. No waiting.',
        description:
          "IntelQI redesigned DYPCET's institutional website from the ground up: responsive architecture, dynamic pages, improved navigation, and enhanced branding. The accompanying admin panel puts full control in DYPCET's hands. Update content, publish announcements, and modify pages in real time with zero technical dependency.",
        highlight:
          'Real-time content control for internal teams. No developer needed for any update.',
        imageUrl: '/stories/substories/dypcet2.webp',
        imageAlt: 'DYPCET redesigned institutional website with modern layout',
      },
      {
        tag: 'AI Interview Portal',
        heading:
          'Configure a full interview round in minutes. AI handles the evaluation.',
        description:
          'The AI Interview Portal allows DYPCET coordinators to define job requirements for specific roles such as Web Development, AI, and Data Science, attach job descriptions, and automatically configure questions aligned to those requirements. Every interview session is recorded centrally, and detailed domain-wise reports give the institution complete analytical insight into each interview cycle.',
        highlight:
          'Role-based interview configuration, AI-assisted evaluation, and detailed domain-wise reports, all in one platform.',
        imageUrl: '/stories/substories/dypcet1.webp',
        imageAlt:
          'DYPCET AI Interview Portal showing role-based configuration and analytics',
      },
    ],
  },
  {
    slug: 'dyptc',
    institutionName: 'D. Y. Patil Technical Campus, Talsande',
    institutionSubtitle: 'Unified Multi-Program Digital Platform',
    quote:
      'IntelQI helped us establish a digitally connected platform that supports both centralized management and independent operational control.',
    logo: '/stories/dyptc1.webp',
    herologo: '/stories/substories/herodyptc.webp',
    institution: 'D. Y. Patil Technical Campus, Talsande',
    location: 'Talsande, Maharashtra',
    category: 'Technical Campus',
    challenge:
      'DYPTC required a digital platform capable of handling multiple academic programs under one roof while maintaining independent administrative control for different teams and coordinators, without fragmenting operations or duplicating infrastructure.',
    journey: [
      'Multiple academic programs operated on disconnected digital systems, creating coordination gaps.',
      'No unified platform existed to manage all programs under a single digital infrastructure.',
      'Different coordinators and departments needed controlled, independent access without overlap.',
      'Website management required technical resources for every update, slowing institutional communication.',
    ],
    journeyDescription:
      'D. Y. Patil Technical Campus, Talsande set out to build a structured and manageable digital platform capable of supporting multiple institutional operations seamlessly. The goal was centralized infrastructure with decentralized control: one system, independent management for every program.',
    solution:
      "IntelQI developed a unified multi-program digital ecosystem featuring a modernized institutional website, a custom admin panel aligned with DYPTC's operational structure, role-based access control for coordinators and administrators, and independent administrative panels for each program, all operating within one unified platform.",
    whatWeBuilt: [
      {
        title: 'Redesigned institutional website',
        description:
          'Responsive design with structured navigation, modern layout, and improved information architecture for all programs.',
        tag: 'Website Revamp',
      },
      {
        title: 'Multi-program admin platform',
        description:
          'Three academic programs unified under one platform, each with its own independent admin environment and operational controls.',
        tag: 'Multi-Program Platform',
      },
      {
        title: 'Role-based access control',
        description:
          'Each coordinator gets precisely the access they need. Department heads manage their programs. Administrators oversee the full platform.',
        tag: 'Role-Based Access',
      },
      {
        title: 'Custom operational admin panel',
        description:
          "Built around DYPTC's specific workflows. Content updates, announcements, and operational changes happen in real time without technical support.",
        tag: 'Admin Panel',
      },
    ],
    whatWeBuiltDescription:
      'One unified digital infrastructure. Independent control for every program. Centralized visibility for leadership.',
    milestones: [
      'Unified digital infrastructure successfully deployed across all programs.',
      'Role-based access control operational. Coordinators manage their departments independently.',
      'Three programs running under one platform with independent admin panels.',
      'Website management simplified. Internal teams update content without technical support.',
      'Improved administrative efficiency and institutional communication established.',
    ],
    outcome:
      'IntelQI helped us establish a digitally connected platform that supports both centralized management and independent operational control.',
    stats: {
      programs: {
        value: '3+',
        label: 'Programs',
        icon: BookOpen,
      },
      departments: {
        value: '6+',
        label: 'Departments',
        icon: Layers,
      },
      admins: {
        value: 'Role-Based',
        label: 'Access Control',
        icon: ShieldCheck,
      },
    },
    testimonial: {
      name: 'Director',
      role: 'D. Y. Patil Technical Campus, Talsande',
      quote:
        'The role-based administrative structure enabled efficient coordination while maintaining operational clarity across different teams.',
    },
    technologies: [
      'Website Revamp',
      'Admin Panel',
      'Role-Based Access',
      'Multi-Program Platform',
    ],
    showcase: [
      {
        tag: 'Unified Platform Architecture',
        heading:
          'Three programs. One platform. Each team runs their own operations.',
        description:
          'IntelQI brought three distinct institutional programs under a single unified digital infrastructure while preserving complete independence for each. Every program operates its own admin environment, managing content, coordinators, and communications, while sharing the same scalable platform backbone that DYPTC controls centrally.',
        highlight:
          'Three programs unified under one platform with fully independent administrative environments for each.',
        imageUrl: '/stories/substories/dyptc2.webp',
        imageAlt:
          'DYPTC multi-program admin dashboard showing unified platform with independent program panels',
      },
      {
        tag: 'Role-Based Access and Admin Panel',
        heading: 'Every coordinator in control. No overlap. No confusion.',
        description:
          'The custom admin panel gives each coordinator precisely the access they need, nothing more, nothing less. Department heads manage their programs, administrators oversee the full platform, and every action is logged for accountability. Content updates, announcements, and operational changes happen in real time without any dependency on technical teams.',
        highlight:
          'Controlled access, real-time management, and full operational accountability built into every role.',
        imageUrl: '/stories/substories/dyptc3.webp',
        imageAlt:
          'DYPTC role-based admin panel showing coordinator access management and content control',
      },
    ],
  },
  {
    slug: 'walchand',
    institutionName: 'Walchand College of Engineering, Sangli',
    institutionSubtitle: 'Digital Learning and Secure Online Examinations',
    quote:
      "IntelQI's solutions helped us establish a more connected, scalable, and secure digital academic environment.",
    logo: '/stories/walchandlog.webp',
    herologo: '/stories/herowalchand.webp',
    institution: 'Walchand College of Engineering, Sangli',
    location: 'Sangli, Maharashtra',
    category: 'Engineering College',
    challenge:
      'Walchand College required a digital platform that could efficiently support both academic learning and secure examination processes. The institution needed scalability, reliability, and examination integrity, all within a single cohesive system.',
    journey: [
      'Digital learning accessibility for students and faculty was limited and fragmented.',
      'No centralized system for managing academic content, assignments, and course activities.',
      'Online examination infrastructure was absent. Large-scale assessments had no secure digital pathway.',
      'Faculty and students lacked a unified platform for interaction and structured academic engagement.',
    ],
    journeyDescription:
      'To enhance its digital academic ecosystem, Walchand College of Engineering partnered with IntelQI to implement a customized Learning Management System and a secure Online Examination System, creating a scalable, technology-driven environment capable of supporting modern learning and large-scale assessments.',
    solution:
      "IntelQI delivered two separate platforms for Walchand College. The first: a customized LMS tailored to Walchand's academic workflows, enabling centralized course content management, online tests, assignment management, and faculty-student digital interaction. The second: an independently deployed Online Examination System capable of conducting secure, simultaneous assessments for large student volumes, with configurable test structures, real-time monitoring, and automated evaluation workflows. Both systems operate as standalone platforms.",
    whatWeBuilt: [
      {
        title: 'Customized Learning Management System',
        description:
          'Centralized course content, online tests, assignments, and faculty-student interaction, all in one structured academic platform.',
        tag: 'LMS',
      },
      {
        title: 'Secure online examination system',
        description:
          'Simultaneous large-scale assessments with configurable test structures, real-time monitoring, and automated evaluation.',
        tag: 'Online Examination',
      },
      {
        title: 'Automated evaluation workflows',
        description:
          'Results processed automatically after every exam cycle. No manual grading overhead. Immediate availability for administrators.',
        tag: 'Automation',
      },
      {
        title: 'Branch and subject analytics',
        description:
          'Students appeared, submissions completed, branch-wise participation, subject-wise performance, and attendance tracking, all in one dashboard.',
        tag: 'Analytics',
      },
    ],
    whatWeBuiltDescription:
      "Two distinct platforms: a customized Learning Management System for academic delivery, and a separate secure Online Examination System, independently deployed and purpose-built for Walchand's needs.",
    milestones: [
      'Customized LMS deployed and operational across all departments.',
      'Secure online examination infrastructure live for large-scale simultaneous assessments.',
      'Automated evaluation and result processing workflows fully active.',
      'Detailed branch-wise and subject-wise performance reports accessible to administration.',
      'Faculty and students connected through a single digital academic environment.',
    ],
    outcome:
      "IntelQI's solutions helped us establish a more connected, scalable, and secure digital academic environment.",
    stats: {
      students: {
        value: '3000+',
        label: 'Students',
        icon: Users,
      },
      faculty: {
        value: '200+',
        label: 'Faculty',
        icon: UserCheck,
      },
      departments: {
        value: '8+',
        label: 'Departments',
        icon: Layers,
      },
    },
    testimonial: {
      name: 'Dr. K. R. Desai',
      role: 'Director, Walchand College of Engineering',
      quote:
        'The customized LMS allowed us to streamline learning resources, assessments, and academic engagement through a single digital platform.',
    },
    technologies: ['LMS', 'Online Examination', 'Analytics', 'Cloud'],
    showcase: [
      {
        tag: 'Learning Management System',
        heading:
          'Faculty deliver. Students engage. Everything tracked in one place.',
        description:
          'Faculty now manage course content, assignments, and assessments from a single structured platform. Students access learning materials, submit work digitally, and receive timely feedback, replacing fragmented systems with a clean, reliable academic loop that scales across all departments simultaneously.',
        highlight:
          'Every course, assignment, and assessment centralized. Faculty and students connected in one system.',
        imageUrl: '/stories/substories/walchand2.webp',
        imageAlt:
          'Walchand College LMS showing course management and student assignment portal',
      },
      {
        tag: 'Secure Examination System',
        heading:
          'Run exams for hundreds of students at once. Secure, monitored, automated.',
        description:
          'The Online Examination System enables Walchand to run simultaneous exams for hundreds of students with complete security and operational control. Configurable test structures, real-time monitoring, automated evaluation, and detailed branch-wise and subject-wise reports give administrators full visibility into every examination cycle, from scheduling to result publication.',
        highlight:
          'Simultaneous large-scale examinations with real-time monitoring and automated evaluation, fully secure.',
        imageUrl: '/stories/substories/walchand1.webp',
        imageAlt:
          'Walchand College examination dashboard with monitoring, branch reports, and result analytics',
      },
    ],
  },
  {
    slug: 'jskb-associates',
    institutionName: 'J.S.K.B. Associates',
    institutionSubtitle: 'Streamlining Financial Operations',
    quote:
      'The IntelQI Finance Module helped us manage financial operations more efficiently through a centralized and structured system.',
    logo: '/stories/jskba.webp',
    herologo: '/stories/herojskb.webp',
    institution: 'J.S.K.B. Associates',
    location: 'Mumbai, Maharashtra',
    category: 'Financial Services',
    challenge:
      'J.S.K.B. required a reliable financial management system capable of handling operational workflows efficiently, managing transactions, maintaining accurate records, and providing leadership with real-time visibility into financial activities.',
    journey: [
      'Financial operations were managed through manual, error-prone processes with limited visibility.',
      'No centralized platform existed to manage transactions, accounting, and financial records together.',
      'Reporting cycles were slow and labour-intensive, preventing timely decision-making.',
      'Operational accuracy and transparency across financial activities were difficult to maintain consistently.',
    ],
    journeyDescription:
      'J.S.K.B. Associates sought to move beyond fragmented financial management and into a structured, intelligent operational model. Precision and control were non-negotiable, and that required a platform purpose-built for end-to-end financial workflow management.',
    solution:
      'IntelQI implemented a customized Finance Module designed to support comprehensive financial management within a unified digital environment. The solution covers transaction and payment management, automated ledger handling, financial record organization, receipt and accounting workflow management, and structured reporting, all in one centralized system.',
    whatWeBuilt: [
      {
        title: 'Transaction and payment management',
        description:
          'Automated validation and processing for every transaction. No manual entry, no reconciliation gaps.',
        tag: 'Finance Module',
      },
      {
        title: 'Automated ledger handling',
        description:
          'Ledger and account management runs automatically, replacing manual reconciliation entirely.',
        tag: 'Automation',
      },
      {
        title: 'Structured financial reporting',
        description:
          'Organized records, receipt management, and accounting workflows all accessible from one centralized dashboard.',
        tag: 'Analytics',
      },
      {
        title: 'Real-time financial visibility',
        description:
          'Leadership accesses financial summaries, account status, and operational reports instantly, not at the end of the month.',
        tag: 'Finance Module',
      },
    ],
    whatWeBuiltDescription:
      'A centralized financial management platform: accuracy, transparency, and operational control in one unified system.',
    milestones: [
      'Centralized Finance Module deployed and fully operational.',
      'Transaction and payment workflows automated with structured validation.',
      'Financial record accuracy and reporting consistency significantly improved.',
      'Leadership now has real-time visibility into financial operations.',
      'Administrative overhead in financial management substantially reduced.',
    ],
    outcome:
      'The IntelQI Finance Module helped us manage financial operations more efficiently through a centralized and structured system.',
    stats: {
      transactions: {
        value: '50K+/mo',
        label: 'Transactions',
        icon: Activity,
      },
      accuracy: {
        value: '99.9%',
        label: 'Accuracy',
        icon: CheckCircle,
      },
      clients: {
        value: '40+',
        label: 'Clients',
        icon: Users,
      },
    },
    testimonial: {
      name: 'Managing Partner',
      role: 'J.S.K.B. Associates',
      quote:
        'We required a reliable financial management system capable of handling our operational workflows efficiently. IntelQI delivered a structured and user-friendly solution aligned with our requirements.',
    },
    technologies: ['Finance Module', 'Analytics', 'Automation', 'Cloud'],
    showcase: [
      {
        tag: 'Transaction and Ledger Management',
        heading:
          'Financial operations run automatically. Zero manual reconciliation.',
        description:
          'J.S.K.B. replaced manual financial tracking with a fully automated Finance Module. Transaction processing, payment management, and ledger handling now run through a validated digital pipeline, eliminating reconciliation errors and ensuring every financial record is accurate, organized, and instantly accessible.',
        highlight:
          'Automated transactions, ledger management, and receipt workflows. Zero manual reconciliation.',
        imageUrl: '/stories/substories/jskba1.webp',
        imageAlt:
          'JSKB Finance Module dashboard with automated transaction and ledger management',
      },
      {
        tag: 'Reporting and Financial Visibility',
        heading:
          'Leadership sees the full financial picture. Right now, not next week.',
        description:
          'Management now has instant access to financial summaries, account status, and operational reports, all from a single dashboard. What once required manual data compilation across spreadsheets is now available in structured, real-time views, enabling faster and more confident financial decisions at every level.',
        highlight:
          'Real-time financial reporting and operational visibility replacing slow manual reporting cycles.',
        imageUrl: '/stories/substories/jkbsa2.webp',
        imageAlt:
          'JSKB financial reporting dashboard with real-time summaries and operational analytics',
      },
    ],
  },
  {
    slug: 'kj-somaiya',
    institutionName: 'K. J. Somaiya School of Engineering',
    institutionSubtitle: 'Structured AI Interview Management at Scale',
    quote:
      'Managing interviews at scale requires both structure and efficiency. The IntelQI AI Interview Portal helped us conduct interviews in a more organized, consistent, and technology-enabled manner.',
    logo: '/stories/kjsomaiya.webp',
    herologo: '/stories/heroKJS.webp',
    institution: 'K. J. Somaiya School of Engineering',
    location: 'Mumbai, Maharashtra',
    category: 'Higher Education',
    challenge:
      'Managing large candidate volumes across multiple interview rounds while maintaining evaluation consistency and reducing unsustainable administrative overhead.',
    journey: [
      'Coordinating interviews across multiple candidates and evaluators simultaneously was operationally complex.',
      'Maintaining consistent evaluation criteria across all rounds was difficult to enforce manually.',
      'Interview records and candidate feedback were tracked manually, creating retrieval and reporting gaps.',
      'Administrative overhead during peak interview cycles was unsustainable and resource-intensive.',
    ],
    journeyDescription:
      'At K. J. Somaiya School of Engineering, the volume and complexity of managing interviews at institutional scale had outpaced manual processes. As the institution continued advancing its technology adoption, a modern, structured interview platform became a critical operational step forward.',
    solution:
      'The IntelQI AI Interview Portal was implemented to create a scalable, structured interview environment. The platform enables AI-assisted structured interviews, a dedicated admin panel for configuring role-specific requirements and job descriptions, centralized candidate records, consistent evaluation frameworks, detailed domain-wise reports, and simplified scheduling for all coordinators.',
    whatWeBuilt: [
      {
        title: 'Role-specific interview configuration',
        description:
          'Add job requirements per role such as Web Dev, AI, or Data Science. Attach job descriptions. Questions are configured automatically.',
        tag: 'AI Interview Portal',
      },
      {
        title: 'AI-assisted structured evaluation',
        description:
          'Every candidate assessed against defined, consistent criteria. No evaluator inconsistency. No subjective drift across rounds.',
        tag: 'AI Interview Portal',
      },
      {
        title: 'Centralized candidate records',
        description:
          'Full interview history, responses, and evaluation scores stored centrally. Searchable and auditable at any time.',
        tag: 'Admin Panel',
      },
      {
        title: 'Domain and branch analytics',
        description:
          'Total candidates interviewed, domain-wise interest distribution, branch-wise participation, completed vs. pending, and evaluation summaries.',
        tag: 'Analytics',
      },
    ],
    whatWeBuiltDescription:
      'A scalable AI-powered interview platform: structured, configurable, and built for institutional volume.',
    milestones: [
      'AI Interview Portal operational with role-specific configuration and job description support.',
      'Faster interview coordination achieved across all simultaneous evaluators.',
      'Consistent and transparent candidate evaluation enforced platform-wide.',
      'Domain-wise and branch-wise interview reports live for institutional insights.',
      'Significant reduction in administrative workload during peak interview periods.',
    ],
    outcome:
      'The platform allowed us to streamline our interview process while maintaining transparency and fairness in candidate evaluations.',
    stats: {
      students: {
        value: '12000+',
        label: 'Students',
        icon: Users,
      },
      faculty: {
        value: '600+',
        label: 'Faculty',
        icon: UserCheck,
      },
      programs: {
        value: '40+',
        label: 'Programs',
        icon: BookOpen,
      },
    },
    testimonial: {
      name: 'Dean of Academics',
      role: 'K. J. Somaiya School of Engineering',
      quote:
        'The platform allowed us to streamline our interview process while maintaining transparency and fairness in candidate evaluations.',
    },
    technologies: [
      'AI Interview Portal',
      'Admin Panel',
      'Analytics',
      'Automation',
    ],
    showcase: [
      {
        tag: 'Role-Based Interview Configuration',
        heading:
          'Set the role, attach a JD, and the platform configures the interview for you.',
        description:
          'The IntelQI admin panel gives K. J. Somaiya coordinators complete control over every interview cycle. Add job requirements for specific roles like Web Development, AI, Data Science, and Cloud Computing, attach job descriptions, and the platform automatically aligns interview questions to those requirements. Every candidate is assessed on criteria that match the role they are being evaluated for.',
        highlight:
          'Role-specific question configuration and job description management, all from a dedicated admin panel.',
        imageUrl: '/stories/substories/kjsomya1.webp',
        imageAlt:
          'K. J. Somaiya AI Interview admin panel showing role configuration and JD management',
      },
      {
        tag: 'Reports and Candidate Intelligence',
        heading:
          'Complete interview analytics. Ready the moment the cycle ends.',
        description:
          'After every interview cycle, coordinators and department heads access detailed reports covering total candidates interviewed, domain-wise interest distribution, branch-wise participation, completed vs. pending interviews, and evaluation summaries. What previously required assembling fragmented spreadsheet data is now available in structured analytical dashboards, instantly.',
        highlight:
          'Branch-wise, domain-wise, and evaluation reports. Complete interview intelligence in one dashboard.',
        imageUrl: '/stories/substories/kjsomya2.webp',
        imageAlt:
          'K. J. Somaiya interview analytics dashboard showing domain reports and candidate evaluation data',
      },
    ],
  },
  {
    slug: 'st-john-baptist-college',
    institutionName: 'St. John the Baptist College',
    institutionSubtitle:
      'AI-Powered Interview Workflows for Institutional Efficiency',
    quote:
      'Adopting the IntelQI AI Interview Portal has helped us bring clarity and efficiency to our interview workflows.',
    logo: '/stories/stjohn.webp',
    herologo: '/stories/substories/herostjohn.webp',
    institution: 'St. John the Baptist College',
    location: 'Palghar, Maharashtra',
    category: 'Engineering College',
    challenge:
      'Conducting interviews efficiently and fairly as candidate volumes grew, while reducing manual coordination effort, enabling role-specific evaluation, and maintaining organized, retrievable interview records with detailed reporting.',
    journey: [
      'Managing growing volumes of candidate interviews through manual, labour-intensive coordination.',
      'No structured way to configure interview questions based on specific job roles or domain requirements.',
      'Evaluation criteria were not consistently applied across different interviewers and rounds.',
      'Interview records, feedback, and outcomes were difficult to retrieve and report on efficiently.',
    ],
    journeyDescription:
      'At St. John the Baptist College, Palghar, fairness and operational efficiency in the interview process were institution-wide priorities. As candidate numbers grew and domain diversity increased, the limitations of manual coordination became a real challenge, one that required a structured, AI-powered solution without adding process complexity.',
    solution:
      'The IntelQI AI Interview Portal provided a fully structured interview management system with AI-assisted evaluations, a dedicated admin panel for role-specific configuration, centralized candidate records, domain-wise analytical reports, and a complete transparent audit trail for every assessment conducted.',
    whatWeBuilt: [
      {
        title: 'Domain-configured AI interviews',
        description:
          'Define job requirements for Full Stack, Web Dev, AI, Data Science, Cloud, and Software Development. Questions are auto-configured per role.',
        tag: 'AI Interview Portal',
      },
      {
        title: 'Consistent structured evaluation',
        description:
          'Every candidate scored against the same criteria. No drift between interviewers. No subjective inconsistency across rounds.',
        tag: 'AI Interview Portal',
      },
      {
        title: 'Centralized candidate records with audit trail',
        description:
          'Full interview history, responses, and evaluation scores stored centrally. Every decision is recorded and auditable.',
        tag: 'Admin Panel',
      },
      {
        title: 'Comprehensive cycle reporting',
        description:
          'Total candidates, domain-wise interest distribution, branch-wise participation, completed vs. pending, evaluation summaries, and shortlisting insights.',
        tag: 'Analytics',
      },
    ],
    whatWeBuiltDescription:
      'A transparent, AI-powered interview management platform: role-configurable, analytically rich, and built for institutional fairness.',
    milestones: [
      'AI Interview Portal live with domain-specific question configuration and JD management.',
      'Structured, transparent evaluation consistently enforced across all interview rounds.',
      'Domain-wise and branch-wise reports operational for every interview cycle.',
      'Candidate records fully centralized. Searchable and auditable at any time.',
      'Administrative effort per interview cycle significantly reduced.',
    ],
    outcome:
      'The platform has enabled us to manage interviews more effectively while ensuring a smooth experience for both evaluators and candidates.',
    stats: {
      students: {
        value: '2500+',
        label: 'Students',
        icon: Users,
      },
      faculty: {
        value: '150+',
        label: 'Faculty',
        icon: UserCheck,
      },
      departments: {
        value: '6+',
        label: 'Departments',
        icon: Layers,
      },
    },
    testimonial: {
      name: 'Principal',
      role: 'St. John the Baptist College, Palghar',
      quote:
        'The platform has enabled us to manage interviews more effectively while ensuring a smooth experience for both evaluators and candidates.',
    },
    technologies: [
      'AI Interview Portal',
      'Admin Panel',
      'Analytics',
      'Automation',
    ],
    showcase: [
      {
        tag: 'AI-Assisted Interviews and Admin Panel',
        heading:
          'Set up domain-specific interviews in minutes. Every session structured and fair.',
        description:
          'St. John the Baptist coordinators use the IntelQI admin panel to define job requirements for specific domains like Full Stack Development, Web Development, AI, Data Science, and Cloud Computing, attach job descriptions, and deploy automatically configured interview sessions. Every session is AI-assisted, ensuring evaluation criteria match the role and every candidate receives a fair, structured assessment.',
        highlight:
          'Domain-specific interview configuration with AI-assisted evaluation. Consistent, fair, and fully automated.',
        imageUrl: '/stories/substories/stjohn1.webp',
        imageAlt:
          'St. John Baptist AI Interview admin panel showing domain configuration and JD management',
      },
      {
        tag: 'Analytics and Reporting',
        heading:
          'Every assessment recorded. Every decision transparent and auditable.',
        description:
          'After every interview cycle, the institution accesses comprehensive reports covering total candidates interviewed, domain-wise interest distribution, branch-wise participation, completed vs. pending interviews, evaluation summaries, and shortlisting insights. Every assessment is recorded with a complete audit trail, giving the institution full transparency into how every decision was made.',
        highlight:
          'Branch-wise, domain-wise, and evaluation reports with complete audit trail. Full institutional transparency.',
        imageUrl: '/stories/substories/stjohn2.webp',
        imageAlt:
          'St. John Baptist interview analytics dashboard with domain reports and evaluation audit trail',
      },
    ],
  },
  {
    slug: 'pillai-university',
    institutionName: 'Pillai University',
    institutionSubtitle:
      'Pillai Pulse 2026 a Multi-Program Entrance Examination at Scale',
    quote:
      "IntelQI's examination portal gave us the infrastructure to conduct a secure, simultaneous entrance examination across four programs with complete operational confidence.",
    logo: '/stories/pillai.webp', // add your logo asset at this path
    herologo: '/stories/heropillai.webp', // add your hero image at this path
    institution: 'Pillai University',
    location: 'New Panvel, Maharashtra',
    category: 'University',
    challenge:
      'Pillai University needed a reliable digital infrastructure to conduct Pillai Pulse 2026, its institution-wide entrance examination, simultaneously across four distinct academic programs. Each program demanded an independent, secure test environment with its own configuration, timing, and candidate pool all running in parallel on a single day without operational overlap or technical failure.',
    journey: [
      'Four separate programs: MBA-PGDM, BTech, BBA-BCA, and BDES, each required an independently configured examination with its own question structure and duration.',
      'BDES required a shorter examination window compared to the other three programs, demanding flexible per-program time configuration.',
      'Coordinating a simultaneous multi-stream entrance exam across a large candidate base required a centralised, fault-tolerant platform.',
      'Maintaining examination integrity and candidate experience at scale, while giving administrators real-time operational visibility, was non-negotiable.',
    ],
    journeyDescription:
      'As Pillai University prepared for its Pillai Pulse 2026 entrance cycle, the institution required a proven examination platform capable of handling multiple programs simultaneously, each with its own test code, timing, and candidate pool, all conducted on a single day with zero compromise on security or reliability.',
    solution:
      "IntelQI's Online Examination Portal was deployed to power Pillai Pulse 2026 across all four academic programs. Each program received an independently configured test environment with a unique test code, program-specific question structure, and a defined examination window. MBA-PGDM, BTech, and BBA-BCA ran simultaneous two-hour sessions, while BDES operated on a separate thirty-minute window all managed through a single administrative interface with real-time monitoring and automated evaluation.",
    whatWeBuilt: [
      {
        title: 'Multi-program examination infrastructure',
        description:
          'Four independent test environments: MBA-PGDM, BTech, BBA-BCA, and BDES, each configured separately with unique test codes and program-specific question sets.',
        tag: 'Online Examination',
      },
      {
        title: 'Configurable examination windows',
        description:
          'MBA-PGDM, BTech, and BBA-BCA ran two-hour sessions simultaneously, while BDES operated on a distinct thirty-minute window, all managed from one admin panel without conflict.',
        tag: 'Admin Panel',
      },
      {
        title: 'Real-time monitoring and automated evaluation',
        description:
          'Live candidate tracking, session monitoring, and automated result processing across all four programs no manual grading, no post-exam reconciliation overhead.',
        tag: 'Automation',
      },
      {
        title: 'Post-examination analytics',
        description:
          'Program-wise participation data, completion rates, and performance summaries available immediately after each examination window closed.',
        tag: 'Analytics',
      },
    ],
    whatWeBuiltDescription:
      'A single, centrally managed examination infrastructure running four independent, simultaneously conducted entrance tests each program isolated, each candidate experience consistent.',
    milestones: [
      'Pillai Pulse 2026 successfully conducted across all four programs on a single day with no downtime.',
      'MBA-PGDM, BTech, and BBA-BCA simultaneous two-hour sessions completed with full operational stability.',
      'BDES thirty-minute examination window independently managed and concluded without overlap.',
      'Automated evaluation and result processing active across all programs immediately post-examination.',
      'Program-wise analytics and participation reports delivered to the administration after each session.',
    ],
    outcome:
      "IntelQI's examination portal gave us the infrastructure to conduct a secure, simultaneous entrance examination across four programs with complete operational confidence.",
    stats: {
      programs: {
        value: '4',
        label: 'Programs',
        icon: BookOpen,
      },
      sessions: {
        value: '1 Day',
        label: 'All Exams',
        icon: Activity,
      },
      evaluation: {
        value: 'Auto',
        label: 'Evaluation',
        icon: Zap,
      },
    },
    testimonial: {
      name: 'Examination Coordinator',
      role: 'Pillai University, New Panvel',
      quote:
        'The platform handled four simultaneous programs without a single operational issue. The administrative visibility and automated evaluation made the entire cycle significantly more manageable.',
    },
    technologies: [
      'Online Examination',
      'Admin Panel',
      'Automation',
      'Analytics',
    ],
    showcase: [
      {
        tag: 'Multi-Program Examination Infrastructure',
        heading:
          'Four programs. One platform. Every candidate in the right exam.',
        description:
          'IntelQI configured four completely independent examination environments for Pillai Pulse 2026: MBA-PGDM, BTech, BBA-BCA, and BDES each with its own test code, question structure, and examination window. All four ran from a single administrative interface, giving coordinators complete visibility across every active session without any cross-program interference.',
        highlight:
          'Independent test environments for each program, managed centrally no overlap, no operational confusion.',
        imageUrl: '/stories/substories/pillai_img1.webp', // add your screenshot here
        imageAlt:
          'Pillai University examination admin panel showing four independently configured program test environments',
      },
      {
        tag: 'Real-Time Monitoring and Automated Results',
        heading:
          'Every session monitored live. Results ready the moment exams close.',
        description:
          'Throughout Pillai Pulse 2026, administrators had real-time visibility into candidate progress across all four programs simultaneously. Automated evaluation removed all manual grading overhead, the moment each examination window closed, results and program-wise participation analytics were immediately available, giving the institution the data it needed without any post-exam processing delay.',
        highlight:
          'Live monitoring across all sessions plus automated evaluation and instant program-wise analytics on completion.',
        imageUrl: '/stories/substories/pillai_img2.webp', // add your screenshot here
        imageAlt:
          'Pillai University examination monitoring dashboard with real-time candidate tracking and automated result summaries',
      },
    ],
  },
  {
    slug: 'dypes-kolhapur',
    institutionName:
      'D. Y. Patil Education Society (Deemed to be University), Kolhapur',
    institutionSubtitle:
      'Unified Digital Presence for a Multi-Institution University',
    quote:
      'IntelQI gave our constituent institutions one coherent digital identity while letting every department retain control over its own content.',
    logo: '/images/dyp-logo.webp',
    herologo: '/images/dyp-logo.webp',
    institution:
      'D. Y. Patil Education Society (Deemed to be University), Kolhapur',
    location: 'Kolhapur, Maharashtra',
    category: 'Deemed University',
    challenge:
      'As a deemed university spanning multiple constituent institutions across health sciences and allied disciplines, D. Y. Patil Education Society needed its digital presence to read as one university rather than a collection of disconnected college pages. Announcements, admissions information, and institutional updates were managed differently in every unit, and no central team had complete visibility or control over what was published where.',
    journey: [
      'Multiple constituent institutions, each presenting itself differently online, with no shared design language or information architecture.',
      'Content updates depended on technical intermediaries, so time-sensitive announcements and admission notices were routinely delayed.',
      'University-level administrators had no single point of control over institution-wide publishing.',
      'Each constituent unit still required genuine autonomy over its own academic content and departmental pages.',
    ],
    journeyDescription:
      'To bring its constituent institutions under one coherent digital identity, D. Y. Patil Education Society partnered with IntelQI for a unified web platform: a university-wide digital presence with centralized administration, structured so that every constituent unit manages its own content within a shared institutional framework.',
    solution:
      'IntelQI delivered a unified institutional platform for the university: a modern, fully responsive web presence covering the university and its constituent institutions, backed by a centralized admin panel with role-based publishing. University administrators control institution-wide content and announcements from one place, while each constituent unit operates its own scoped admin environment for departmental pages, faculty listings, and academic updates: autonomy inside a single, consistent framework.',
    whatWeBuilt: [
      {
        title: 'Unified university web platform',
        description:
          'One responsive digital presence for the university and its constituent institutions, with a shared design language and consistent information architecture throughout.',
        tag: 'Website Revamp',
      },
      {
        title: 'Centralized admin panel',
        description:
          'University-level control over institution-wide content, announcements, and admissions information, published in real time, no developer in the loop.',
        tag: 'Admin Panel',
      },
      {
        title: 'Scoped unit-level publishing',
        description:
          'Each constituent institution manages its own departmental pages, faculty listings, and academic updates inside its own role-based admin environment.',
        tag: 'Role-Based Access',
      },
      {
        title: 'Institution-wide publishing visibility',
        description:
          'A single view of what is published across every unit, so university administration always knows what each institution is presenting.',
        tag: 'Analytics',
      },
    ],
    whatWeBuiltDescription:
      'A single university platform with two layers of control: centralized administration for the university, scoped independent publishing for every constituent institution.',
    milestones: [
      'University and constituent institutions unified under one coherent, fully responsive digital presence.',
      'Centralized admin panel operational for university-wide announcements and admissions content.',
      'Constituent units publishing independently through scoped, role-based admin environments.',
      'Time-sensitive notices now published in real time without technical intermediaries.',
      'University administration has complete visibility over institution-wide publishing.',
    ],
    outcome:
      'IntelQI gave our constituent institutions one coherent digital identity while letting every department retain control over its own content.',
    stats: {
      institutions: {
        value: 'Multi',
        label: 'Constituent Units',
        icon: Layers,
      },
      publishing: {
        value: 'Real-time',
        label: 'Content Publishing',
        icon: Zap,
      },
      control: {
        value: 'Role-based',
        label: 'Access Control',
        icon: ShieldCheck,
      },
    },
    testimonial: {
      name: 'Administrative Office',
      role: 'D. Y. Patil Education Society (Deemed to be University), Kolhapur',
      quote:
        'The centralized panel changed how we communicate as a university. Announcements go out the moment they are ready, and every constituent institution still runs its own pages.',
    },
    technologies: [
      'Website Revamp',
      'Admin Panel',
      'Role-Based Access',
      'Analytics',
    ],
    showcase: [],
  },
]

export type CustomerStory = (typeof customerStories)[number]

/** Slugs are load-bearing: components/developer-section.tsx deep-links all seven. */
export const storySlugs = customerStories.map((s) => s.slug)

export function getStory(slug: string): CustomerStory | undefined {
  return customerStories.find((s) => s.slug === slug)
}

/**
 * Listing cards for /customer-stories, moved verbatim out of that page.
 * These are NOT duplicates of the records above — the listing carries its own
 * editorial summary copy (title, client, description, modules). They live in
 * the same module so the two views can be kept deliberately in step rather
 * than drifting in two files, which is what was happening before.
 */
export const storyCards = [
  {
    id: 1,
    slug: 'dypcet',
    title: 'DYPCET - Building a Smarter Digital Ecosystem',
    shortTitle: 'DYPCET',
    client: 'D. Y. Patil College of Engineering and Technology, Kolhapur',
    description:
      'A fully redesigned institutional website, a centralized admin panel for independent content management, and an AI Interview Portal with role-specific question configuration, job description management, and domain-wise analytics.',
    thumbnail: '/stories/dyp.webp',
    category: 'Engineering College',
    modules: [
      'Website Revamp',
      'Admin Panel',
      'AI Interview Portal',
      'Domain Analytics',
    ],
    quote:
      'The solutions implemented by IntelQI helped us modernize both our institutional operations and digital experience.',
  },
  {
    id: 2,
    slug: 'dyptc',
    title: 'DYPTC Talsande - Unified Multi-Program Digital Platform',
    shortTitle: 'DYPTC Talsande',
    client: 'D. Y. Patil Technical Campus, Talsande',
    description:
      'A modern, scalable digital ecosystem with a redesigned website, custom admin panel built to institutional requirements, role-based access control for coordinators, and three academic programs unified under one platform with independent admin environments.',
    thumbnail: '/stories/dyptc1.webp',
    category: 'Technical Campus',
    modules: [
      'Website Development',
      'Custom Admin Panel',
      'Role-Based Access',
      'Multi-Program Management',
    ],
    quote:
      'IntelQI helped us establish a digitally connected platform that supports both centralized management and independent operational control.',
  },
  {
    id: 3,
    slug: 'walchand',
    title:
      'Walchand College of Engineering - Digital Learning and Secure Examinations',
    shortTitle: 'Walchand College',
    client: 'Walchand College of Engineering, Sangli',
    description:
      'A customized LMS for centralized academic delivery combined with a Secure Online Examination System supporting simultaneous large-scale assessments, automated evaluation, real-time monitoring, and branch-wise and subject-wise analytics.',
    thumbnail: '/stories/walchandlog.webp',
    category: 'Engineering College',
    modules: [
      'Customized LMS',
      'Secure Exam System',
      'Automated Evaluation',
      'Branch-wise Analytics',
    ],
    quote:
      "IntelQI's solutions helped us establish a more connected, scalable, and secure digital academic environment.",
  },
  {
    id: 4,
    slug: 'jskb-associates',
    title: 'J.S.K.B. Associates - Streamlining Financial Operations',
    shortTitle: 'J.S.K.B. Associates',
    client: 'J.S.K.B. Associates, Mumbai',
    description:
      'A customized Finance Module covering transaction and payment management, automated ledger handling, receipt and accounting workflows, financial record organization, and real-time reporting for leadership visibility.',
    thumbnail: '/stories/jskba.webp',
    category: 'Financial Services',
    modules: [
      'Finance Module',
      'Automated Workflows',
      'Ledger Management',
      'Real-time Reporting',
    ],
    quote:
      'The IntelQI Finance Module helped us manage financial operations more efficiently through a centralized and structured system.',
  },
  {
    id: 5,
    slug: 'kj-somaiya',
    title: 'K. J. Somaiya - AI-Powered Interview Management at Scale',
    shortTitle: 'KJ Somaiya',
    client: 'K. J. Somaiya School of Engineering, Mumbai',
    description:
      'AI Interview Portal with a dedicated admin panel for configuring job descriptions and domain-specific questions per role, skill and interest-based candidate analysis, centralized interview dashboard, and advanced branch-wise and domain-wise reporting.',
    thumbnail: '/stories/kjsomaiya.webp',
    category: 'Higher Education',
    modules: [
      'AI Interview Portal',
      'Admin Panel & Job Config',
      'Domain-wise Analytics',
      'Advanced Reporting',
    ],
    quote:
      'Managing interviews at scale requires both structure and efficiency. The IntelQI AI Interview Portal helped us conduct interviews in a more organized, consistent, and technology-enabled manner.',
  },
  {
    id: 6,
    slug: 'st-john-baptist-college',
    title:
      'St. John the Baptist College - AI Interview Management with Deep Analytics',
    shortTitle: 'St. John the Baptist',
    client: 'St. John the Baptist College, Palghar',
    description:
      'AI Interview Portal with admin-configurable job descriptions and domain-specific question sets for Full Stack, Web Dev, AI, Data Science, Cloud and Software Development, skill and interest-based candidate profiling, centralized dashboard, and branch-wise and domain-wise reporting.',
    thumbnail: '/stories/stjohn.webp',
    category: 'Engineering College',
    modules: [
      'AI Interview Portal',
      'Admin Panel & Job Config',
      'Candidate Analytics',
      'Branch-wise Reporting',
    ],
    quote:
      'Adopting the IntelQI AI Interview Portal has helped us bring clarity and efficiency to our interview workflows.',
  },
  {
    id: 7, // increment from your last entry (adjust if needed)
    slug: 'pillai-university',
    title:
      'Pillai University - Pillai Pulse 2026 Multi-Program Entrance Examination',
    shortTitle: 'Pillai University',
    client: 'Pillai University, New Panvel',
    description:
      'Online Examination Portal powering Pillai Pulse 2026 a simultaneous entrance exams across four programs (MBA-PGDM, BTech, BBA-BCA, and BDES) with independently configured test environments, flexible per-program timing windows, real-time session monitoring, automated evaluation, and program-wise participation analytics.',
    thumbnail: '/stories/pillai.webp',
    category: 'University',
    modules: [
      'Online Examination',
      'Multi-Program Test Config',
      'Real-Time Monitoring',
      'Automated Evaluation & Analytics',
    ],
    quote:
      "IntelQI's examination portal gave us the infrastructure to conduct a secure, simultaneous entrance examination across four programs with complete operational confidence.",
  },
  {
    id: 8,
    slug: 'dypes-kolhapur',
    title:
      'D. Y. Patil Education Society - One University, One Digital Identity',
    shortTitle: 'D. Y. Patil University, Kolhapur',
    client: 'D. Y. Patil Education Society (Deemed to be University), Kolhapur',
    description:
      'A unified web platform bringing the university and its constituent institutions under one coherent digital identity, with a centralized admin panel for university-wide publishing and scoped, role-based admin environments giving every constituent unit independent control over its own content.',
    thumbnail: '/images/dyp-logo.webp',
    category: 'Deemed University',
    modules: [
      'Website Revamp',
      'Centralized Admin Panel',
      'Role-Based Access',
      'Unified Platform',
    ],
    quote:
      'IntelQI gave our constituent institutions one coherent digital identity while letting every department retain control over its own content.',
  },
]
